import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';
import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon, BuildingOffice2Icon, TicketIcon, UserPlusIcon, CommandLineIcon, PhoneIcon, ArrowTopRightOnSquareIcon, StarIcon, TrashIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import RefreshEcommerceScriptsModal from '../components/RefreshEcommerceScriptsModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
/** Same-origin launcher route — opens a real browser tab on backoffice, then redirects to client preview. */
const getEditorLauncherUrl = (websiteId) => `/open-editor/${websiteId}`;

const PLAN_TYPE_LABELS = {
  pro: 'Marketing',
  landing_page: 'Landing Page',
  ecommerce: 'eCommerce',
  trial: 'Trial',
  free: 'Free',
  enterprise: 'Enterprise'
};

const getPlanLabel = (row) => {
  if (!row.plan_type) return '—';
  return PLAN_TYPE_LABELS[row.plan_type] || row.plan_type;
};

const getPlanBadgeClass = (row) => {
  if (!row.plan_type) return 'bg-gray-100 text-gray-500';
  if (row.plan_subscription_status === 'past_due') return 'bg-yellow-100 text-yellow-800';
  const classes = {
    landing_page: 'bg-green-100 text-green-800',
    pro: 'bg-blue-100 text-blue-800',
    ecommerce: 'bg-purple-100 text-purple-800',
    trial: 'bg-sky-100 text-sky-800',
    free: 'bg-gray-100 text-gray-700',
    enterprise: 'bg-indigo-100 text-indigo-800'
  };
  return classes[row.plan_type] || 'bg-gray-100 text-gray-800';
};

const Websites = () => {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [deploymentFilter, setDeploymentFilter] = useState('');
  const [designVersionFilter, setDesignVersionFilter] = useState('');
  const [siteTypeFilter, setSiteTypeFilter] = useState('');
  const [originFilter, setOriginFilter] = useState('');
  const [businessInfoFilter, setBusinessInfoFilter] = useState(false);
  const [openTicketFilter, setOpenTicketFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [businessInfoModal, setBusinessInfoModal] = useState(null); // holds the website row to show
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [favoriteWebsites, setFavoriteWebsites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [togglingFavorite, setTogglingFavorite] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchWebsites = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(statusFilter && { status: statusFilter }),
        ...(deploymentFilter && { deployment_status: deploymentFilter }),
        ...(designVersionFilter && { design_version: designVersionFilter }),
        ...(siteTypeFilter && { site_type: siteTypeFilter }),
        ...(originFilter && { origin: originFilter }),
        ...(businessInfoFilter && { has_business_info: 'true' }),
        ...(openTicketFilter && { has_open_ticket: openTicketFilter }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/websites?${params}`);
      const rows = response.data.data.websites || [];
      setWebsites(rows);
      setTotalPages(response.data.data.pagination.totalPages);
      // Only merge favorites-in on this page. Never delete from the global set here:
      // a row with is_favorite:false may simply be stale (e.g. after toggleFavorite) or
      // absent from the capped favorites panel while still favorited. Removals are
      // handled only by toggleFavorite.
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        rows.forEach((w) => {
          if (w.is_favorite) next.add(w.id);
        });
        return next;
      });
    } catch (error) {
      console.error('Error fetching websites:', error);
      toast.error('Failed to fetch websites');
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const params = new URLSearchParams({
        favorite_only: 'true',
        limit: '50',
        page: '1',
        sort_by: sortBy,
        sort_order: sortOrder
      });
      const response = await axios.get(`${API_BASE_URL}/api/admin/websites?${params}`);
      const favs = response.data.data.websites || [];
      // Panel is capped at 50 and re-sorted with the main table — never prune favoriteIds
      // from that window; favorites outside the top 50 must keep their star in the grid.
      setFavoriteWebsites(favs);
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        favs.forEach((w) => next.add(w.id));
        return next;
      });
    } catch (error) {
      console.error('Error fetching favorite websites:', error);
    }
  };

  const toggleFavorite = async (e, websiteId) => {
    e.stopPropagation();
    setTogglingFavorite(websiteId);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/admin/websites/favorites/${websiteId}`);
      const { isFavorite } = response.data;
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFavorite) next.add(websiteId);
        else next.delete(websiteId);
        return next;
      });
      toast.success(isFavorite ? 'Added to favorites' : 'Removed from favorites');
      await fetchFavorites();
      await fetchWebsites();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    } finally {
      setTogglingFavorite(null);
    }
  };

  useEffect(() => {
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  useEffect(() => {
    fetchWebsites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, deploymentFilter, designVersionFilter, siteTypeFilter, originFilter, businessInfoFilter, openTicketFilter, sortBy, sortOrder, debouncedSearchTerm]);

  const handleClearFilters = () => {
    setStatusFilter('');
    setDeploymentFilter('');
    setDesignVersionFilter('');
    setSiteTypeFilter('');
    setOriginFilter('');
    setBusinessInfoFilter(false);
    setOpenTicketFilter('');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
  };

  const hasActiveFilters = statusFilter || deploymentFilter || designVersionFilter || siteTypeFilter || originFilter || businessInfoFilter || openTicketFilter || searchTerm;

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const toggleSelect = (websiteId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(websiteId)) next.delete(websiteId);
      else next.add(websiteId);
      return next;
    });
  };

  const toggleSelectAll = (rows) => {
    const ids = rows.map((row) => row.id).filter(Boolean);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allOn = ids.length > 0 && ids.every((id) => next.has(id));
      ids.forEach((id) => {
        if (allOn) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || deleting) return;

    const label = ids.length === 1 ? 'this website' : `${ids.length} websites`;
    if (!window.confirm(`Delete ${label}? This cannot be undone and will permanently remove the websites and their associated data.`)) {
      return;
    }
    if (!window.confirm('Final warning. The selected websites, their versions, and their deployments will be permanently deleted. Continue?')) {
      return;
    }

    try {
      setDeleting(true);
      const response = await axios.post(`${API_BASE_URL}/api/admin/websites/bulk-delete`, {
        websiteIds: ids,
      });
      const deleted = response.data?.data?.deleted || [];
      const failed = response.data?.data?.failed || [];
      const gone = new Set([
        ...deleted,
        ...failed.filter((row) => row.notFound).map((row) => row.websiteId),
      ]);

      setSelectedIds((prev) => {
        const next = new Set(prev);
        gone.forEach((id) => next.delete(id));
        return next;
      });

      if (failed.length === 0) {
        toast.success(deleted.length === 1 ? 'Website deleted' : `Deleted ${deleted.length} websites`);
      } else if (deleted.length === 0) {
        toast.error(failed.length === 1 ? (failed[0].error || 'Failed to delete website') : `Failed to delete ${failed.length} websites`);
      } else {
        toast.error(`Deleted ${deleted.length}, failed ${failed.length}`);
      }

      await fetchFavorites();
      await fetchWebsites();
    } catch (error) {
      console.error('Error deleting websites:', error);
      toast.error(error.response?.data?.error || 'Failed to delete websites');
    } finally {
      setDeleting(false);
    }
  };

  // Get origin label from computed backend fields (avoids parsing huge requirements JSONB)
  const getWebsiteOriginLabel = (row) => {
    if (row.is_figma_import) return 'Imported (Figma)';
    if (row.is_site_clone) return 'Cloned';
    if (row.is_url_import) return 'Imported (URL)';
    return 'Generated';
  };

  const columns = [
    { 
      key: 'name', 
      label: 'Name',
      width: '140px',
      cellClassName: 'truncate',
      render: (row) => (
        <div className="flex items-center gap-1 min-w-0">
          <button
            type="button"
            className={`flex-shrink-0 p-0.5 rounded transition-colors ${
              favoriteIds.has(row.id)
                ? 'text-yellow-500 hover:text-yellow-600'
                : 'text-gray-300 hover:text-yellow-500'
            }`}
            title={favoriteIds.has(row.id) ? 'Remove from favorites' : 'Add to favorites'}
            onClick={(e) => toggleFavorite(e, row.id)}
            disabled={togglingFavorite === row.id}
          >
            {togglingFavorite === row.id ? (
              <div className="w-3.5 h-3.5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
            ) : favoriteIds.has(row.id) ? (
              <StarIconSolid className="h-3.5 w-3.5" />
            ) : (
              <StarIcon className="h-3.5 w-3.5" />
            )}
          </button>
          <span className="font-medium truncate" title={row.name}>
            {row.name}
          </span>
          <a
            href={getEditorLauncherUrl(row.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 text-primary-600 hover:text-primary-800 transition-colors"
            title="Open Zappy editor in a new browser tab"
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </a>
        </div>
      )
    },
    { 
      key: 'user_name', 
      label: 'Owner',
      width: '140px',
      render: (row) => (
        <div className="truncate">
          <p className="font-medium truncate" title={row.user_name}>{row.user_name}</p>
          <p className="text-xs text-gray-500 truncate" title={row.user_email}>{row.user_email}</p>
          {row.user_phone && (
            <p className="text-xs text-gray-400 truncate flex items-center gap-0.5" title={row.user_phone}>
              <PhoneIcon className="h-3 w-3 flex-shrink-0" />
              {row.user_phone}
            </p>
          )}
        </div>
      )
    },
    { 
      key: 'industry', 
      label: 'Industry',
      width: '120px',
      cellClassName: 'truncate',
      render: (row) => (
        <span className="truncate block" title={row.industry}>
          {row.industry}
        </span>
      )
    },
    {
      key: 'design_version',
      label: 'Design',
      width: '55px',
      render: (row) => {
        const isV2 = !!row.is_ai_designs_v2;
        return (
          <span
            className={`inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded ${
              isV2 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
            }`}
            title={isV2 ? 'AI Designs V2' : 'AI Designs V1'}
          >
            {isV2 ? 'V2' : 'V1'}
          </span>
        );
      }
    },
    {
      key: 'site_type',
      label: 'Type',
      width: '80px',
      render: (row) => {
        const type = (row.site_type || 'marketing').toLowerCase();
        const isEcom = type === 'ecommerce';
        const isCatalog = isEcom && row.ecommerce_mode === 'catalog';
        const isCourses = isEcom && row.ecommerce_mode === 'courses';
        const isLanding = type === 'landing_page';
        const label = isCourses
          ? 'Courses'
          : isCatalog ? 'Catalog'
          : isEcom ? 'E-comm'
          : isLanding ? 'Landing'
          : 'Marketing';
        const title = isCourses
          ? 'Online courses site (lessons + enrollments + certificates)'
          : isCatalog ? 'Catalog site (product display only)'
          : isEcom ? 'E-commerce site'
          : isLanding ? 'Landing page (single page)'
          : 'Marketing site';
        const badgeClass = isCourses
          ? 'bg-indigo-100 text-indigo-800'
          : isCatalog ? 'bg-purple-100 text-purple-800'
          : isEcom ? 'bg-amber-100 text-amber-800'
          : isLanding ? 'bg-teal-100 text-teal-800'
          : 'bg-blue-100 text-blue-800';
        return (
          <span
            className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${badgeClass}`}
            title={title}
          >
            {label}
          </span>
        );
      }
    },
    {
      key: 'plan_type',
      label: 'Plan',
      width: '85px',
      render: (row) => (
        <span
          className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${getPlanBadgeClass(row)}`}
          title={row.plan_subscription_status === 'past_due' ? 'Subscription past due' : undefined}
        >
          {getPlanLabel(row)}
        </span>
      )
    },
    {
      key: 'origin',
      label: 'Origin',
      width: '85px',
      render: (row) => {
        const label = getWebsiteOriginLabel(row);
        const sourceUrl = row.import_source_url;
        
        // Different colors for different origins
        let badgeClasses = 'bg-gray-100 text-gray-700';
        if (label === 'Cloned') {
          badgeClasses = 'bg-cyan-100 text-cyan-800';
        } else if (label.startsWith('Imported')) {
          badgeClasses = 'bg-emerald-100 text-emerald-800';
        }
        
        return (
          <span
            className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${badgeClasses}`}
            title={sourceUrl ? `Source: ${sourceUrl}` : label}
          >
            {label === 'Imported (Figma)' ? 'Figma' : label === 'Imported (URL)' ? 'URL' : label}
          </span>
        );
      }
    },
    { 
      key: 'status', 
      label: 'Status',
      width: '85px',
      render: (row) => (
        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
          row.status === 'completed' ? 'bg-green-100 text-green-800' :
          row.status === 'generating' ? 'bg-blue-100 text-blue-800' :
          row.status === 'suspended' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {row.status}
        </span>
      )
    },
    { 
      key: 'deployment_status', 
      label: 'Deploy',
      width: '75px',
      render: (row) => (
        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
          row.deployment_status === 'deployed' ? 'bg-green-100 text-green-800' :
          row.deployment_status === 'deploying' ? 'bg-blue-100 text-blue-800' :
          row.deployment_status === 'failed' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {row.deployment_status || 'pending'}
        </span>
      )
    },
    {
      key: 'versions',
      label: 'Ver',
      width: '45px',
      render: (row) => (
        <span className="text-xs font-medium text-gray-700">
          {row.current_version || 1}
        </span>
      )
    },
    {
      key: 'has_seen_site',
      label: 'Seen',
      width: '55px',
      render: (row) => {
        const seen = !!row.has_seen_site;
        const tourSeen = !!row.user_has_seen_dashboard_tour;
        const edited = !!row.has_edited_site;
        const reasons = [];
        if (tourSeen) reasons.push('completed dashboard tour');
        if (edited) reasons.push('edited the site past baseline');
        const reason = seen
          ? `User has viewed the site (${reasons.join(', ')})`
          : 'User has not opened this site yet';
        return (
          <span
            className={`inline-flex items-center justify-center w-6 h-5 text-xs font-semibold rounded ${
              seen ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
            }`}
            title={reason}
          >
            {seen ? '1' : '0'}
          </span>
        );
      }
    },
    {
      key: 'version_size',
      label: 'Size',
      width: '70px',
      render: (row) => {
        const bytes = row.total_versions_size_bytes || 0;
        const formatSize = (bytes) => {
          if (bytes === 0) return '-';
          if (bytes < 1024) return `${bytes} B`;
          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
          if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
          return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
        };
        return (
          <span className={`text-xs font-medium ${bytes > 50 * 1024 * 1024 ? 'text-red-600' : bytes > 10 * 1024 * 1024 ? 'text-orange-600' : 'text-gray-700'}`}>
            {formatSize(bytes)}
          </span>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '85px',
      render: (row) => {
        const d = new Date(row.created_at);
        return (
          <div className="text-xs text-gray-600 leading-tight">
            <div>{d.toLocaleDateString()}</div>
            <div className="text-gray-400">{d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        );
      }
    },
    {
      key: 'links',
      label: 'Links',
      width: '90px',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-1.5 flex-wrap">
          {row.business_info && Object.keys(row.business_info).length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setBusinessInfoModal(row); }}
              className="text-amber-600 hover:text-amber-800 transition-colors"
              title="Business Info"
            >
              <BuildingOffice2Icon className="h-4 w-4" />
            </button>
          )}
          {(row.render_url || row.custom_domain_url) && (
            <a
              href={row.custom_domain_url || row.render_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
              title="Live Site"
              onClick={(e) => e.stopPropagation()}
            >
              🌐
            </a>
          )}
          {row.github_url && (
            <a
              href={row.github_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-800"
              title="GitHub"
              onClick={(e) => e.stopPropagation()}
            >
              📦
            </a>
          )}
          <Link
            to={`/websites/${row.id}`}
            className="text-primary-600 hover:text-primary-800"
            title="Details"
            onClick={(e) => e.stopPropagation()}
          >
            📋
          </Link>
        </div>
      )
    }
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Websites Management</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRefreshModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors"
              title="Refresh scripts for all e-commerce and catalog websites"
            >
              <CommandLineIcon className="w-4 h-4" />
              Refresh E-comm Scripts
            </button>
            <button
              onClick={fetchWebsites}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, domain, owner, account, email, phone, or industry..."
                className="input-field pl-10 pr-10 w-full"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field text-sm"
            >
              <option value="">All Status</option>
              <option value="generating">Generating</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="suspended">Suspended</option>
            </select>
            
            <select
              value={deploymentFilter}
              onChange={(e) => {
                setDeploymentFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field text-sm"
            >
              <option value="">All Deployments</option>
              <option value="pending">Pending</option>
              <option value="deploying">Deploying</option>
              <option value="deployed">Deployed</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={designVersionFilter}
              onChange={(e) => {
                setDesignVersionFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field text-sm"
            >
              <option value="">All Designs</option>
              <option value="v1">V1 (Classic)</option>
              <option value="v2">V2 (AI Designs)</option>
            </select>

            <select
              value={siteTypeFilter}
              onChange={(e) => {
                setSiteTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field text-sm"
            >
              <option value="">All Types</option>
              <option value="marketing">Marketing</option>
              <option value="ecommerce">E-commerce</option>
              <option value="landing_page">Landing Page</option>
            </select>

            <select
              value={originFilter}
              onChange={(e) => {
                setOriginFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field text-sm"
            >
              <option value="">All Origins</option>
              <option value="generated">Generated</option>
              <option value="cloned">Cloned</option>
              <option value="figma">Imported (Figma)</option>
              <option value="url">Imported (URL)</option>
            </select>
          </div>

          {/* Toggle Filters */}
          <div className="mt-3 flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={businessInfoFilter}
                onChange={(e) => {
                  setBusinessInfoFilter(e.target.checked);
                  setCurrentPage(1);
                }}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
              />
              Has Business Info
            </label>
            <div className="flex items-center gap-1.5 text-sm text-gray-700">
              <TicketIcon className="h-4 w-4 text-orange-500" />
              <select
                value={openTicketFilter}
                onChange={(e) => {
                  setOpenTicketFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-field text-sm py-1 pl-2 pr-7"
              >
                <option value="">All Tickets / Leads</option>
                <option value="true">Has Open Ticket / Lead</option>
                <option value="false">No Open Ticket / Lead</option>
                <option value="ticket">Open Tickets Only</option>
                <option value="new_lead">New Leads Only</option>
                <option value="qualified">Qualified Leads Only</option>
                <option value="ready_to_buy">Ready to Buy Only</option>
              </select>
            </div>
            <div className="flex items-center gap-3 ml-2 text-xs text-gray-500 border-l border-gray-200 pl-3">
              <button
                onClick={() => { setOpenTicketFilter(openTicketFilter === 'ticket' ? '' : 'ticket'); setCurrentPage(1); }}
                className={`flex items-center gap-1 hover:opacity-80 transition-opacity ${openTicketFilter === 'ticket' ? 'opacity-100 font-semibold' : 'opacity-70'}`}
                title="Filter: has open ticket"
              >
                <TicketIcon className="h-3.5 w-3.5 text-orange-500" />
                <span>Ticket</span>
              </button>
              <button
                onClick={() => { setOpenTicketFilter(openTicketFilter === 'new_lead' ? '' : 'new_lead'); setCurrentPage(1); }}
                className={`flex items-center gap-1 hover:opacity-80 transition-opacity ${openTicketFilter === 'new_lead' ? 'opacity-100 font-semibold' : 'opacity-70'}`}
                title="Filter: has lead (New / Contacted / Replied)"
              >
                <UserPlusIcon className="h-3.5 w-3.5 text-blue-500" />
                <span>New Lead</span>
              </button>
              <button
                onClick={() => { setOpenTicketFilter(openTicketFilter === 'qualified' ? '' : 'qualified'); setCurrentPage(1); }}
                className={`flex items-center gap-1 hover:opacity-80 transition-opacity ${openTicketFilter === 'qualified' ? 'opacity-100 font-semibold' : 'opacity-70'}`}
                title="Filter: has lead (Qualified / Demo / Considering)"
              >
                <UserPlusIcon className="h-3.5 w-3.5 text-purple-500" />
                <span>Qualified</span>
              </button>
              <button
                onClick={() => { setOpenTicketFilter(openTicketFilter === 'ready_to_buy' ? '' : 'ready_to_buy'); setCurrentPage(1); }}
                className={`flex items-center gap-1 hover:opacity-80 transition-opacity ${openTicketFilter === 'ready_to_buy' ? 'opacity-100 font-semibold' : 'opacity-70'}`}
                title="Filter: has lead (Ready to Buy)"
              >
                <UserPlusIcon className="h-3.5 w-3.5 text-emerald-500" />
                <span>Ready to Buy</span>
              </button>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={handleClearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="mb-4 flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <span className="text-sm font-medium text-red-900">
              {selectedIds.size === 1 ? '1 website selected' : `${selectedIds.size} websites selected`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                disabled={deleting}
                className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <TrashIcon className="w-4 h-4" />
                {deleting ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </button>
            </div>
          </div>
        )}

        {/* Favorites (shared with dashboard My Sites) */}
        {favoriteWebsites.length > 0 && !hasActiveFilters && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <StarIconSolid className="w-5 h-5 text-yellow-500" />
              <h2 className="text-lg font-semibold text-gray-900">Favorites</h2>
              <span className="text-sm text-gray-500">({favoriteWebsites.length})</span>
            </div>
            <div className="bg-white rounded-lg shadow overflow-x-auto ring-2 ring-yellow-200">
              <Table
                columns={columns}
                data={favoriteWebsites}
                loading={false}
                emptyMessage="No favorites"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                onRowClick={(row) => navigate(`/websites/${row.id}`)}
                selectable
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                selectAllLabel="Select all favorites"
              />
            </div>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-300" />
              <span className="text-sm text-gray-500 font-medium">All Websites</span>
              <div className="flex-1 h-px bg-gray-300" />
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <Table
            columns={columns}
            data={websites}
            loading={loading}
            emptyMessage="No websites found"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onRowClick={(row) => navigate(`/websites/${row.id}`)}
            selectable
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            selectAllLabel="Select all websites on this page"
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {/* Business Info Modal */}
      {businessInfoModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setBusinessInfoModal(null)}
            />
            
            {/* Modal */}
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <BuildingOffice2Icon className="h-5 w-5 text-amber-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Business Info
                    </h3>
                  </div>
                  <button
                    onClick={() => setBusinessInfoModal(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                
                <p className="text-sm text-gray-500 mb-4">
                  {businessInfoModal.name} — {businessInfoModal.industry || 'No industry'}
                </p>

                <div className="space-y-3">
                  {(() => {
                    const info = businessInfoModal.business_info || {};
                    const fields = [
                      { label: 'Business Name', value: info.name },
                      { label: 'Email', value: info.email, href: info.email ? `mailto:${info.email}` : null },
                      { label: 'Phone', value: info.phone, href: info.phone ? `tel:${info.phone}` : null },
                      { label: 'Address', value: info.address },
                      { label: 'Business Hours', value: info.hours },
                      { label: 'Language', value: info.language },
                    ];
                    
                    const visibleFields = fields.filter(f => f.value);
                    
                    return visibleFields.map((field, idx) => (
                      <div key={idx} className="border-b border-gray-100 pb-2 last:border-0">
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          {field.label}
                        </dt>
                        <dd className="mt-0.5 text-sm text-gray-900">
                          {field.href ? (
                            <a href={field.href} className="text-primary-600 hover:text-primary-800 hover:underline">
                              {field.value}
                            </a>
                          ) : (
                            field.value
                          )}
                        </dd>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={() => setBusinessInfoModal(null)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RefreshEcommerceScriptsModal
        isOpen={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
      />
    </AdminLayout>
  );
};

export default Websites;
